import { createSystem, defaultConfig, mergeConfigs } from '@chakra-ui/react'
import { button } from '@reward-platform/lift/components/ChakraButton/themes/britishAirways'
import { snackbar } from '@reward-platform/lift/components/Snackbar/themes/britishAirways'
import { stickyBanner } from '@reward-platform/lift/components/StickyBanner/themes/britishAirways'
import { customTabs } from '@reward-platform/lift/components/Tabs/themes/britishAirways'
import { imageCard } from '~/components/search-and-results/Card/themes/britishAirways'
import { footer } from '~/components/shared/Footer/themes/britishAirways'
import { baseTheme } from './base'

const primaryBlue = 'rgb(var(--chakra-colors-rgb-blue))'
const midnightBlue = 'rgb(var(--chakra-colors-rgb-midnightBlue))'
const outlineColor = primaryBlue

const focusOutline = {
  outlineColor,
  outlineWidth: 1,
  borderRadius: 'sm',
  outlineStyle: 'solid',
}

export const britishAirwaysTheme = createSystem(
  defaultConfig,
  mergeConfigs(baseTheme, {
    globalCss: {
      'html, body, div#__next': {
        color: 'brand.primary.midnightBlue',
      },
      body: {
        '& input': {
          _placeholder: {
            color: 'brand.baseGray.25',
          },
        },
        fontWeight: 'light',
      },
      'p, h1, h2, h3, h4, h5, h6': {
        fontWeight: 'light',
      },
      a: {
        color: 'brand.highlight.blue',
      },
      '*:focus-visible': focusOutline,
    },

    theme: {
      tokens: {
        shadows: {
          brand: {
            boxShadow: {
              value: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
            offsetShadow: {
              value: '0 4px 4px -2px rgb(0 0 0 / 40%)',
            },
          },
        },

        fonts: {
          heading: {
            value: 'Mylius Modern',
          },
          body: {
            value: 'Open Sans',
          },
        },

        fontSizes: {
          '4xl-5': {
            value: '2.75rem',
          },
        },

        spacing: {
          '4-5': {
            value: '1.125rem',
          },
        },

        borders: {
          '10px': {
            value: '10px solid',
          },
        },

        colors: {
          rgb: {
            blue: {
              value: '46, 92, 153',
            },
            backgroundBlue500: {
              value: '234, 239, 245',
            },
            alphaBlue: {
              value: '244, 247, 250',
            },
            red: {
              value: '206, 33, 15',
            },
            midnightBlue: {
              value: '2, 27, 65',
            },
            gray5: {
              value: '250, 250, 250',
            },
            gray10: {
              value: '236, 236, 237',
            },
            gray25: {
              value: '216, 217, 220',
            },
            gray50: {
              value: '182, 183, 189',
            },
            gray75: {
              value: '204, 209, 217',
            },
            gray100: {
              value: '113, 115, 127',
            },
            highlightBlue: {
              value: '38, 113, 211',
            },
            highlightRed: {
              value: '232, 17, 28',
            },
            white: {
              value: '255, 255, 255',
            },
            cloudWhite: {
              value: '249, 249, 249',
            },
            black: {
              value: '0, 0, 0',
            },
            alertPink: {
              value: '204, 0, 102',
            },
            confirmationGreen: {
              value: '0, 128, 88',
            },
            baCoolGrey4: {
              value: '187, 188, 188',
            },
            coolgrey2: {
              value: '208, 208, 206',
            },
            green: {
              value: '0, 128, 88',
            },
            red2: {
              value: '183, 62, 62',
            },
          },
          brand: {
            primary: {
              blue: {
                value: 'rgb({colors.rgb.blue})',
              },
              alphaBlue: {
                value: 'rgb({colors.rgb.alphaBlue})',
              },
              red: {
                value: 'rgb({colors.rgb.red})',
              },
              midnightBlue: {
                value: midnightBlue,
              },
            },
            baseGray: {
              5: {
                value: 'rgb({colors.rgb.gray5})',
              },
              10: {
                value: 'rgb({colors.rgb.gray10})',
              },
              25: {
                value: 'rgb({colors.rgb.gray25})',
              },
              50: {
                value: 'rgb({colors.rgb.gray50})',
              },
              75: {
                value: 'rgb({colors.rgb.gray75})',
              },
              100: {
                value: 'rgb({colors.rgb.gray100})',
              },
            },
            highlight: {
              blue: {
                value: 'rgb({colors.rgb.highlightBlue})',
              },
              red: {
                value: 'rgb({colors.rgb.highlightRed})',
              },
            },
            alpha: {
              blue: {
                value: 'rgba({colors.rgb.blue}, 0.1)',
              },
              gray10: {
                value: 'rgba({colors.rgb.gray10}, 0.5)',
              },
            },
            cloudWhite: {
              value: 'rgb({colors.rgb.cloudWhite})',
            },
            white: {
              value: 'rgb({colors.rgb.white})',
            },
            black: {
              value: 'rgb({colors.rgb.black})',
            },
            alertPink: {
              value: 'rgb({colors.rgb.alertPink})',
            },
            confirmationGreen: {
              value: 'rgb({colors.rgb.confirmationGreen})',
            },
            coolgrey4: {
              value: 'rgb({colors.rgb.baCoolGrey4})',
            },
            coolgrey2: {
              value: 'rgb({colors.rgb.coolgrey2})',
            },
            green: {
              value: 'rgb({colors.rgb.green})',
            },
            red2: {
              value: 'rgb({colors.rgb.red2})',
            },
            icon: {
              value: midnightBlue,
            },
            inputIcon: {
              value: midnightBlue,
            },
            disabledIcon: {
              value: 'rgb({colors.rgb.gray25})',
            },
            focusOutline: {
              value: outlineColor,
            },
            backgroundBlue500: {
              value: 'rgb({colors.rgb.backgroundBlue500})',
            },
          },
          error: {
            primary: {
              value: '#CC0066',
            },
          },
        },
      },

      recipes: {
        // Shared
        button,
      },

      slotRecipes: {
        // Deprecated
        customTabs,

        // Shared
        snackbar,
        stickyBanner,
        footer,

        // Search & Results
        imageCard,
      },
    },
  })
)
